import React, { useRef, useState } from 'react';
import {
    HiArrowLeft, HiArrowRight, HiArrowUp, HiArrowUpRight, HiBeaker, HiCheck, HiCheckCircle, HiRocketLaunch, HiGlobeAlt, HiOutlineDocument, HiOutlineCloudArrowUp,
    HiMap, HiPlus, HiPuzzlePiece, HiSparkles, HiXMark,
} from 'react-icons/hi2';
import { fileProblem, uploadKnowledgeFile } from '../api/knowledge-upload';
import {
    DOCUMENTS_SKIPPED, WEBSITES_SKIPPED, customRequestAnswer, descriptionAnswer, documentSourceId, documentsAnswer,
    goalAnswer, normalizeWebsite, websitesAnswer,
} from '../model/onboarding';
import { PUBLISH_REQUEST, TEST_REQUEST } from '../model/publishing';
import { Spinner } from './activity';
import { chatCard, chatEyebrow, chatField, chatFocus, chatGhost, chatIconTile, chatLabel, chatPrimary, chatSecondary, chatTone } from './chat-theme';

const card = chatCard('knowledge');
const field = chatField;
const primary = chatPrimary;
const ghost = chatGhost;
const ghostPill = chatSecondary;
const footer = 'mt-3 flex items-center justify-end gap-1.5 border-t border-slate-200 pt-3';

function ErrorText({ children }) {
    return children ? <p role="alert" className="mt-1.5 text-xs text-red-600 dark:text-red-400">{children}</p> : null;
}

const GOAL_ICONS = { faq: HiGlobeAlt, journey: HiMap, integration: HiPuzzlePiece };

/** Onboarding's first question: what kind of agent the builder wants. Only the FAQ goal asks for websites. */
export function GoalCard({ request, onSend, disabled }) {
    return (
        <div className="grid gap-2.5 sm:grid-cols-2" role="group" aria-label="Kind of agent">
            {request.options.map(option => {
                const Icon = GOAL_ICONS[option.id] || HiSparkles;
                return (
                    <button key={option.id} type="button" disabled={disabled} onClick={() => onSend(goalAnswer(option))}
                        className={`group h-full text-left transition duration-200 motion-safe:enabled:hover:-translate-y-0.5 enabled:hover:border-slate-300 enabled:hover:bg-white disabled:cursor-default disabled:opacity-60 ${chatCard('knowledge')} ${chatFocus}`}>
                        <span className="flex items-center justify-between gap-2">
                            <span className={`${chatIconTile('planning')} h-8 w-8 transition-colors ${disabled ? '' : 'group-hover:bg-gray-900 group-hover:text-brand-lime group-hover:ring-gray-900 dark:group-hover:bg-brand-lime dark:group-hover:ring-brand-lime'}`}>
                                <Icon aria-hidden="true" className="h-4 w-4" />
                            </span>
                            {!disabled && <HiArrowRight aria-hidden="true" className="h-4 w-4 shrink-0 text-gray-300 transition-all group-hover:translate-x-0.5 group-hover:text-gray-900" />}
                        </span>
                        <span className="mt-3 block text-[0.9rem] font-semibold text-gray-900">{option.title}</span>
                        {option.description && <span className="mt-1 block text-[0.8rem] leading-5 text-gray-600">{option.description}</span>}
                    </button>
                );
            })}
        </div>
    );
}

/** For any goal but the website/docs FAQ: the builder describes the agent, sent to the copilot as a custom request. */
export function CustomRequestCard({ request, onSend, disabled }) {
    const [description, setDescription] = useState('');
    const submit = event => {
        event.preventDefault();
        if (!disabled && description.trim()) onSend(customRequestAnswer(request, description));
    };
    return (
        <form onSubmit={submit} className={card} aria-label="Describe your agent">
            <fieldset disabled={disabled}>
                <textarea value={description} onChange={event => setDescription(event.target.value)} rows={5} maxLength={4000}
                    aria-label="Describe the agent you want" autoFocus placeholder={request.placeholder || 'Describe what you need'}
                    className={`${field} w-full resize-none leading-6`} />
                <div className={footer}>
                    <button type="submit" className={primary} disabled={!description.trim()}>
                        <HiSparkles className="h-3.5 w-3.5" /> Build my agent
                    </button>
                </div>
            </fieldset>
        </form>
    );
}

/** Onboarding step 1: one or more website links, or skip. Nothing is fetched until setup starts. */
export function WebsitesCard({ request, onSend, disabled }) {
    const [urls, setUrls] = useState(['']);
    const [error, setError] = useState('');
    const filled = urls.map(url => url.trim()).filter(Boolean);

    const update = (index, value) => {
        setError('');
        setUrls(current => current.map((url, i) => (i === index ? value : url)));
    };

    const submit = event => {
        event.preventDefault();
        if (disabled || !filled.length) return;
        try {
            onSend(websitesAnswer([...new Set(filled.map(normalizeWebsite))]));
        } catch (err) {
            setError(err.message);
        }
    };

    return (
        <form onSubmit={submit} className={card} aria-label="Business websites">
            <fieldset disabled={disabled} className="space-y-2">
                {urls.map((url, index) => (
                    <div key={index} className="flex items-center gap-2">
                        <HiGlobeAlt aria-hidden="true" className="h-4 w-4 shrink-0 text-gray-400" />
                        <input
                            value={url}
                            onChange={event => update(index, event.target.value)}
                            aria-label={`Website ${index + 1}`}
                            placeholder="example.com"
                            autoComplete="url"
                            inputMode="url"
                            maxLength={2000}
                            autoFocus={index === urls.length - 1}
                            className={field}
                        />
                        {urls.length > 1 && (
                            <button type="button" onClick={() => setUrls(current => current.filter((_, i) => i !== index))}
                                aria-label={`Remove website ${index + 1}`} className="rounded-lg p-1.5 text-gray-400 hover:bg-slate-200/60 hover:text-gray-900">
                                <HiXMark className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                ))}
                {urls.length < request.maxUrls && (
                    <button type="button" onClick={() => setUrls(current => [...current, ''])}
                        className="ml-6 inline-flex items-center gap-1 text-[0.8rem] font-medium text-gray-600 hover:text-gray-900">
                        <HiPlus className="h-3.5 w-3.5" /> Add another website
                    </button>
                )}
                <ErrorText>{error}</ErrorText>
                <div className={footer}>
                    <button type="button" className={ghost} onClick={() => onSend(WEBSITES_SKIPPED)}>Skip</button>
                    <button type="submit" className={primary} disabled={!filled.length}>Continue <HiArrowRight className="h-3.5 w-3.5" /></button>
                </div>
            </fieldset>
        </form>
    );
}

const STATUS_LABEL = { uploading: 'Uploading…', done: 'Uploaded', failed: 'Failed' };

/**
 * Onboarding step 2: documents about the business, or skip. Each file becomes its own knowledge
 * source, uploaded exactly like Knowledge → Files; the copilot is told their source ids.
 */
export function DocumentsCard({ request, appId, onSend, disabled }) {
    const inputRef = useRef(null);
    const [files, setFiles] = useState([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const accept = request.accept.map(ext => `.${ext}`).join(',');
    const blocked = files.some(entry => entry.problem);

    const choose = event => {
        const picked = Array.from(event.target.files || []);
        event.target.value = '';
        setError('');
        setFiles(current => [...current, ...picked.map(file => ({
            file, id: documentSourceId(file.name), status: 'idle', problem: fileProblem(file, request.accept),
        }))].slice(0, request.maxFiles));
    };

    const patch = (id, change) => setFiles(current => current.map(entry => (entry.id === id ? { ...entry, ...change } : entry)));

    const upload = async () => {
        if (!appId) {
            setError('Open the application first.');
            return;
        }
        setBusy(true);
        setError('');
        const uploaded = [];
        try {
            for (const entry of files) {
                if (entry.status !== 'done') {
                    patch(entry.id, { status: 'uploading' });
                    try {
                        await uploadKnowledgeFile(appId, {
                            dataSourceId: entry.id, accept: request.accept, ocr: false,
                            description: `Business document "${entry.file.name}", shared during onboarding.`,
                        }, entry.file);
                    } catch (err) {
                        patch(entry.id, { status: 'failed' });
                        setError((err && err.message) || 'The upload failed. Try again.');
                        return;
                    }
                    patch(entry.id, { status: 'done' });
                }
                uploaded.push({ dataSourceId: entry.id, fileName: entry.file.name });
            }
            onSend(documentsAnswer(uploaded));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className={card} aria-label="Business documents">
            <fieldset disabled={disabled || busy}>
                {files.length > 0 && (
                    <ul className="mb-2 space-y-1">
                        {files.map(entry => (
                            <li key={entry.id} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-2.5 py-1.5 text-[0.85rem]">
                                {entry.status === 'uploading' ? <Spinner className="h-4 w-4 shrink-0" />
                                    : entry.status === 'done' ? <HiCheckCircle className="h-4 w-4 shrink-0 text-lime-600 dark:text-brand-lime" />
                                    : <HiOutlineDocument className="h-4 w-4 shrink-0 text-gray-500" />}
                                <span className="min-w-0 flex-1 truncate text-gray-900">{entry.file.name}</span>
                                {entry.problem
                                    ? <span className="text-xs text-red-600 dark:text-red-400">{entry.problem}</span>
                                    : STATUS_LABEL[entry.status] && <span className="text-xs text-gray-500">{STATUS_LABEL[entry.status]}</span>}
                                {entry.status !== 'done' && (
                                    <button type="button" onClick={() => setFiles(current => current.filter(item => item.id !== entry.id))}
                                        aria-label={`Remove ${entry.file.name}`} className="rounded p-0.5 text-gray-400 hover:text-gray-900">
                                        <HiXMark className="h-4 w-4" />
                                    </button>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
                <input ref={inputRef} type="file" multiple accept={accept} onChange={choose} className="hidden" aria-label="Choose documents" />
                {files.length < request.maxFiles && (
                    <button type="button" onClick={() => inputRef.current?.click()}
                        className="flex w-full items-center gap-2 rounded-xl border border-dashed border-slate-300 bg-white/70 px-3 py-3 text-left text-[0.85rem] text-gray-700 transition-colors hover:border-slate-400 hover:bg-white">
                        <HiOutlineCloudArrowUp className="h-4 w-4 shrink-0" />
                        <span>{files.length ? 'Add more documents' : 'Choose documents'}</span>
                        <span className="ml-auto text-xs text-gray-400">{request.accept.map(ext => ext.toUpperCase()).join(', ')} · up to {request.maxFiles}</span>
                    </button>
                )}
                <ErrorText>{error}</ErrorText>
                <div className={footer}>
                    <button type="button" className={ghost} onClick={() => onSend(DOCUMENTS_SKIPPED)} disabled={busy || files.some(entry => entry.status === 'done')}>Skip</button>
                    <button type="button" className={primary} onClick={upload} disabled={!files.length || blocked || busy}>
                        {busy ? <><Spinner className="h-3.5 w-3.5" /> Uploading…</> : <>Upload and continue <HiArrowRight className="h-3.5 w-3.5" /></>}
                    </button>
                </div>
            </fieldset>
        </div>
    );
}

/** Asked only when the builder skipped both websites and documents: what the business does. */
export function DescriptionCard({ onSend, disabled }) {
    const [description, setDescription] = useState('');
    const submit = event => {
        event.preventDefault();
        if (!disabled && description.trim()) onSend(descriptionAnswer(description));
    };
    return (
        <form onSubmit={submit} className={card} aria-label="About your business">
            <fieldset disabled={disabled}>
                <textarea value={description} onChange={event => setDescription(event.target.value)} rows={4} maxLength={2000}
                    aria-label="Describe your business" autoFocus
                    placeholder="e.g. We run a bike repair shop in Winnipeg. Customers ask about prices, opening hours and booking a service."
                    className={`${field} w-full resize-none leading-6`} />
                <div className={footer}>
                    <button type="submit" className={primary} disabled={!description.trim()}>Continue <HiArrowRight className="h-3.5 w-3.5" /></button>
                </div>
            </fieldset>
        </form>
    );
}

/** The agent is being set up in the background; the builder may leave (they are emailed). */
export function SetupNotice({ setup, working }) {
    const sources = setup.websites.length + setup.documents.length;
    return (
        <div role="status" className={`${chatCard('planning')} text-[0.875rem]`}>
            <p className="flex items-center gap-2.5 font-semibold text-gray-900">
                <span className={chatIconTile(working ? 'planning' : 'success')}>
                    {working ? <Spinner className="h-3.5 w-3.5" /> : <HiCheck className="h-4 w-4" />}
                </span>
                {working ? 'Setting up your agent' : 'Agent setup finished'}
            </p>
            {setup.message && <p className="mt-2 leading-relaxed text-gray-600">{setup.message}</p>}
            {sources > 0 && (
                <p className="mt-3 flex flex-wrap gap-1.5">
                    {setup.websites.map(url => (
                        <span key={url} className={`${chatLabel()} max-w-full truncate`}>
                            <HiGlobeAlt className="h-3.5 w-3.5 shrink-0" /> {new URL(url).hostname}
                        </span>
                    ))}
                    {setup.documents.length > 0 && (
                        <span className={chatLabel()}>
                            <HiOutlineDocument className="h-3.5 w-3.5" /> {setup.documents.length} document{setup.documents.length === 1 ? '' : 's'}
                        </span>
                    )}
                </p>
            )}
        </div>
    );
}

/** The use cases found for the business: pick one (and edit its prompt), or ask for something else. */
export function SuggestionsCard({ request, onSend, disabled, actionable }) {
    const [selected, setSelected] = useState(null);
    const [prompt, setPrompt] = useState('');
    const [custom, setCustom] = useState('');
    const locked = disabled || !actionable;

    if (selected) {
        return (
            <div className={chatCard('knowledge')}>
                <button type="button" onClick={() => setSelected(null)} className="mb-2 inline-flex items-center gap-1 text-[0.8rem] text-gray-500 hover:text-gray-900">
                    <HiArrowLeft className="h-3.5 w-3.5" /> All use cases
                </button>
                <label htmlFor={`onboarding-prompt-${selected.id}`} className="mb-1.5 block text-[0.875rem] font-medium text-gray-900">{selected.title}</label>
                <p className="mb-2 text-xs text-gray-500">Review or edit the request. Copilot uses it to build your agent.</p>
                <textarea id={`onboarding-prompt-${selected.id}`} value={prompt} onChange={event => setPrompt(event.target.value)}
                    rows={8} maxLength={6000} className={`${field} w-full leading-6`} disabled={locked} />
                <div className={footer}>
                    <button type="button" className={primary} disabled={locked || !prompt.trim()} onClick={() => onSend({ message: prompt.trim() })}>
                        <HiSparkles className="h-3.5 w-3.5" /> Build this agent
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {(request.businessName || request.businessSummary) && (
                <div className={`${chatCard('planning')} text-[0.875rem]`}>
                    <p className={`mb-2 flex items-center gap-1.5 ${chatEyebrow}`}><HiGlobeAlt className="h-3.5 w-3.5" /> Business overview</p>
                    {request.businessName && <p className="font-medium text-gray-900">{request.businessName}</p>}
                    {request.businessSummary && <p className="mt-0.5 leading-relaxed text-gray-600">{request.businessSummary}</p>}
                    {(request.websites.length > 0 || request.documents.length > 0) && (
                        <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                            <span>Based on</span>
                            {request.websites.map(url => (
                                <a key={url} href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline">
                                    <HiGlobeAlt className="h-3.5 w-3.5" /> {new URL(url).hostname}
                                </a>
                            ))}
                            {/* Website and uploaded-document sources alike: everything the agent can now answer from. */}
                            {request.documents.length > 0 && <span className="inline-flex items-center gap-1"><HiOutlineDocument className="h-3.5 w-3.5" /> {request.documents.length} knowledge source{request.documents.length === 1 ? '' : 's'}</span>}
                        </p>
                    )}
                </div>
            )}
            {request.items.length > 0 && (
                <div className="grid gap-2.5 sm:grid-cols-2" role="group" aria-label="Suggested use cases">
                    {request.items.map(item => (
                        <button key={item.id} type="button" disabled={locked}
                            onClick={() => { setSelected(item); setPrompt(item.prompt); }}
                            className={`group h-full text-left transition duration-200 motion-safe:enabled:hover:-translate-y-0.5 enabled:hover:border-slate-300 enabled:hover:bg-white disabled:cursor-default ${chatCard('knowledge')} ${chatFocus}`}>
                            <span className="flex items-center justify-between gap-2">
                                <span className={`inline-flex rounded-md border px-2 py-0.5 text-[11px] font-medium ${chatTone('knowledge').badge}`}>{item.category}</span>
                                {!locked && <HiArrowUpRight aria-hidden="true" className="h-4 w-4 shrink-0 text-gray-300 transition-colors group-hover:text-gray-900" />}
                            </span>
                            <span className="mt-2 block text-[0.9rem] font-semibold text-gray-900">{item.title}</span>
                            {item.description && <span className="mt-1 block text-[0.8rem] leading-5 text-gray-600">{item.description}</span>}
                        </button>
                    ))}
                </div>
            )}
            {actionable && (
                <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Next steps">
                    <button type="button" className={ghostPill} disabled={locked} onClick={() => onSend({ message: TEST_REQUEST, display: 'Test my agent' })}>
                        <HiBeaker className="h-3.5 w-3.5" /> Test my agent
                    </button>
                    <button type="button" className={ghostPill} disabled={locked} onClick={() => onSend({ message: PUBLISH_REQUEST, display: 'Publish my agent' })}>
                        <HiRocketLaunch className="h-3.5 w-3.5" /> Publish my agent
                    </button>
                </div>
            )}
            {actionable && (
                <form className={card} onSubmit={event => { event.preventDefault(); if (!locked && custom.trim()) onSend({ message: custom.trim() }); }}>
                    <label htmlFor="onboarding-custom-request" className="block text-[0.85rem] font-medium text-gray-900">
                        {request.items.length ? 'Something else? Describe what you need' : 'Describe what you need'}
                    </label>
                    <div className="mt-2 flex items-end gap-2">
                        <textarea id="onboarding-custom-request" value={custom} onChange={event => setCustom(event.target.value)}
                            rows={2} maxLength={4000} disabled={locked} placeholder="e.g. An agent that answers questions about our opening hours and takes bookings"
                            className={`${field} resize-none`} />
                        <button type="submit" aria-label="Send custom request" className={`${primary} pr-2.5`} disabled={locked || !custom.trim()}>
                            <HiArrowUp className="h-3.5 w-3.5" />
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
}
